/**
 * Affiliate attribution store. With marketing consent (service "affiliate")
 * the attribution is kept 30 days in localStorage; without consent only in
 * memory for the current visit.
 */
import { supabase } from '@/integrations/supabase/client';
import { hasServiceConsent } from '@/lib/consent';
import { isExpired, nextAttribution, type Attribution, type AffiliateSource } from '@/lib/affiliate-rules';

const KEY = 'h2h_affiliate';
let memory: Attribution | null = null;
let welcomePending: string | null = null;
const listeners = new Set<() => void>();

const consented = () => hasServiceConsent('affiliate');

function readStored(): Attribution | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Attribution;
    if (isExpired(a)) { localStorage.removeItem(KEY); return null; }
    return a;
  } catch { return null; }
}

function persist() {
  try {
    if (memory && consented()) localStorage.setItem(KEY, JSON.stringify(memory));
    else localStorage.removeItem(KEY);
  } catch { /* ignore */ }
}

export function getAffiliate(): Attribution | null {
  if (!memory && consented()) memory = readStored();
  return memory;
}

export function setAffiliate(slug: string, name: string, source: AffiliateSource) {
  memory = nextAttribution(getAffiliate(), { slug, name, source, at: Date.now() });
  persist();
  listeners.forEach((l) => l());
}

export function subscribeAffiliate(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }

/** Welcome note: shown once per visit after a short link or ?ref=. */
export function markWelcome(name: string) { welcomePending = name; listeners.forEach((l) => l()); }
export function takeWelcome(): string | null { return welcomePending; }
export function dismissWelcome() { welcomePending = null; listeners.forEach((l) => l()); }

if (typeof window !== 'undefined') {
  window.addEventListener('h2h-consent-change', () => { getAffiliate(); persist(); });
}

export interface PartnerInfo { slug: string; name: string }
let partnersPromise: Promise<PartnerInfo[]> | null = null;
export function loadPartners(): Promise<PartnerInfo[]> {
  if (!partnersPromise) {
    partnersPromise = Promise.resolve(supabase.rpc('get_affiliate_partners'))
      .then(({ data }) => (data ?? []) as PartnerInfo[])
      .catch(() => { partnersPromise = null; return []; });
  }
  return partnersPromise;
}

export async function resolveAffiliateCode(code: string): Promise<{ slug: string; name: string; kind: string } | null> {
  try {
    const { data } = await supabase.rpc('resolve_affiliate_code', { _code: code });
    return (data && data[0]) ?? null;
  } catch { return null; }
}

export async function affiliateFirstCode(slug: string): Promise<string | null> {
  try {
    const { data } = await supabase.rpc('affiliate_first_code', { _slug: slug });
    return (data as string | null) ?? null;
  } catch { return null; }
}

/** Metadata for the checkout session. */
export function affiliateMetadata(): Record<string, string> {
  const a = getAffiliate();
  return a ? { affiliate: a.slug, affiliate_source: a.source } : {};
}
